/**
 * Microsoft Authenticator Card Component
 *
 * Card component for managing Microsoft Authenticator authentication method
 * with all 5 Azure Portal features:
 * 1. OTP toggle (Cycle 2)
 * 2. Number matching - display-only, permanently enabled by Microsoft (Cycle 3)
 * 3. Display app information with target selection (Cycle 4)
 * 4. Display location information with target selection (Cycle 5)
 * 5. Companion applications with target selection (Cycle 6)
 *
 * TDD Cycles 1-7 COMPLETE:
 * - Cycles 1-6: All 5 features implemented with 30 tests
 * - Cycle 7: API integration with initialConfig/onConfigChange (7 tests)
 * Total: 37 integration tests, all passing
 */

import { useState, useEffect, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Body2,
  Switch,
  Button,
  Field,
  Dropdown,
  Option,
  shorthands,
} from '@fluentui/react-components';
import { PhoneDesktopRegular } from '@fluentui/react-icons';
import { BaseAuthenticationMethodCard } from './BaseAuthenticationMethodCard';

const useStyles = makeStyles({
  configSection: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
    ...shorthands.padding(tokens.spacingVerticalM, 0, 0, 0),
  },
  sectionHeader: {
    fontWeight: tokens.fontWeightSemibold,
    ...shorthands.margin(tokens.spacingVerticalS, 0, tokens.spacingVerticalXS, 0),
  },
  buttonGroup: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalS),
    ...shorthands.padding(tokens.spacingVerticalM, 0, 0, 0),
  },
  hintText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    ...shorthands.margin(tokens.spacingVerticalXS, 0, 0, 0),
  },
});

interface MicrosoftAuthenticatorCardProps {
  tenantId: string;
  initialConfig?: Record<string, unknown>; // Will be properly typed in later cycles
  onConfigChange?: (config: Record<string, unknown>) => void;
}

export const MicrosoftAuthenticatorCard: React.FC<MicrosoftAuthenticatorCardProps> = ({
  tenantId: _tenantId, // Prefix with underscore - will be used in later cycles
  initialConfig,
  onConfigChange,
}) => {
  const styles = useStyles();

  // State for form values
  const [isEnabled, setIsEnabled] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isOtpEnabled, setIsOtpEnabled] = useState(false);
  const [displayAppInfoState, setDisplayAppInfoState] = useState<string>('default');
  const [displayAppInfoTarget, setDisplayAppInfoTarget] = useState<string>('all');
  const [displayLocationState, setDisplayLocationState] = useState<string>('disabled');
  const [displayLocationTarget, setDisplayLocationTarget] = useState<string>('all');
  const [companionAppsState, setCompanionAppsState] = useState<string>('default');
  const [companionAppsTarget, setCompanionAppsTarget] = useState<string>('all');

  // Store initial values for cancel functionality
  const initialValuesRef = useRef({
    isEnabled: false,
    isOtpEnabled: false,
    displayAppInfoState: 'default',
    displayAppInfoTarget: 'all',
    displayLocationState: 'disabled',
    displayLocationTarget: 'all',
    companionAppsState: 'default',
    companionAppsTarget: 'all',
  });

  // Load configuration from initialConfig prop
  useEffect(() => {
    if (!initialConfig) return;

    // Extract state
    const enabled = initialConfig.state === 'enabled';
    setIsEnabled(enabled);

    // Extract configuration
    const config = initialConfig.configuration as Record<string, unknown> | undefined;
    if (!config) return;

    // Extract OTP setting
    const softwareOath = config.is_software_oath_enabled;
    if (typeof softwareOath === 'boolean') {
      setIsOtpEnabled(softwareOath);
    }

    // Extract feature settings
    const featureSettings = config.feature_settings as Record<string, unknown> | undefined;
    if (!featureSettings) return;

    // Display App Information
    const appInfo = featureSettings.display_app_information_required_state as Record<string, unknown> | undefined;
    if (appInfo) {
      const state = appInfo.state as string;
      setDisplayAppInfoState(state || 'default');

      const includeTarget = appInfo.include_target as Record<string, unknown> | undefined;
      if (includeTarget) {
        const targetType = includeTarget.target_type as string;
        setDisplayAppInfoTarget(targetType === 'all_users' ? 'all' : 'group');
      }
    }

    // Display Location Information
    const locationInfo = featureSettings.display_location_information_required_state as Record<string, unknown> | undefined;
    if (locationInfo) {
      const state = locationInfo.state as string;
      setDisplayLocationState(state || 'disabled');

      const includeTarget = locationInfo.include_target as Record<string, unknown> | undefined;
      if (includeTarget) {
        const targetType = includeTarget.target_type as string;
        setDisplayLocationTarget(targetType === 'all_users' ? 'all' : 'group');
      }
    }

    // Companion Applications
    const companionApps = featureSettings.companion_applications_allowed_state as Record<string, unknown> | undefined;
    if (companionApps) {
      const state = companionApps.state as string;
      setCompanionAppsState(state || 'default');

      const includeTarget = companionApps.include_target as Record<string, unknown> | undefined;
      if (includeTarget) {
        const targetType = includeTarget.target_type as string;
        setCompanionAppsTarget(targetType === 'all_users' ? 'all' : 'group');
      }
    }

    // Store initial values for cancel functionality
    initialValuesRef.current = {
      isEnabled: enabled,
      isOtpEnabled: typeof softwareOath === 'boolean' ? softwareOath : false,
      displayAppInfoState: appInfo?.state as string || 'default',
      displayAppInfoTarget: (appInfo?.include_target as Record<string, unknown>)?.target_type === 'all_users' ? 'all' : 'group',
      displayLocationState: locationInfo?.state as string || 'disabled',
      displayLocationTarget: (locationInfo?.include_target as Record<string, unknown>)?.target_type === 'all_users' ? 'all' : 'group',
      companionAppsState: companionApps?.state as string || 'default',
      companionAppsTarget: (companionApps?.include_target as Record<string, unknown>)?.target_type === 'all_users' ? 'all' : 'group',
    };
  }, [initialConfig]);

  const handleSave = () => {
    // Transform component state to API format
    const config = {
      state: isEnabled ? 'enabled' : 'disabled',
      configuration: {
        is_software_oath_enabled: isOtpEnabled,
        feature_settings: {
          display_app_information_required_state: {
            state: displayAppInfoState,
            include_target: {
              target_type: displayAppInfoTarget === 'all' ? 'all_users' : 'group',
              id: displayAppInfoTarget === 'all' ? 'all' : 'group-id-placeholder',
            },
          },
          display_location_information_required_state: {
            state: displayLocationState,
            include_target: {
              target_type: displayLocationTarget === 'all' ? 'all_users' : 'group',
              id: displayLocationTarget === 'all' ? 'all' : 'group-id-placeholder',
            },
          },
          companion_applications_allowed_state: {
            state: companionAppsState,
            include_target: {
              target_type: companionAppsTarget === 'all' ? 'all_users' : 'group',
              id: companionAppsTarget === 'all' ? 'all' : 'group-id-placeholder',
            },
          },
        },
      },
    };

    // Call onConfigChange callback if provided
    if (onConfigChange) {
      onConfigChange(config);
    }

    // Update initial values after successful save
    initialValuesRef.current = {
      isEnabled,
      isOtpEnabled,
      displayAppInfoState,
      displayAppInfoTarget,
      displayLocationState,
      displayLocationTarget,
      companionAppsState,
      companionAppsTarget,
    };

    setIsEditing(false);
  };

  const handleCancel = () => {
    // Revert to initial values
    setIsEnabled(initialValuesRef.current.isEnabled);
    setIsOtpEnabled(initialValuesRef.current.isOtpEnabled);
    setDisplayAppInfoState(initialValuesRef.current.displayAppInfoState);
    setDisplayAppInfoTarget(initialValuesRef.current.displayAppInfoTarget);
    setDisplayLocationState(initialValuesRef.current.displayLocationState);
    setDisplayLocationTarget(initialValuesRef.current.displayLocationTarget);
    setCompanionAppsState(initialValuesRef.current.companionAppsState);
    setCompanionAppsTarget(initialValuesRef.current.companionAppsTarget);

    setIsEditing(false);
  };

  return (
    <BaseAuthenticationMethodCard
      icon={<PhoneDesktopRegular />}
      title="Microsoft Authenticator"
      description="Approve sign-ins via push notifications or use the app for passwordless authentication."
      securityLevel={3}
      securityText="Moderate security"
      isEnabled={isEnabled}
      onToggle={(checked) => setIsEnabled(checked)}
      showConfigureButton={!isEditing}
      onConfigure={() => setIsEditing(true)}
    >
      {/* Configuration section - shown when enabled and editing */}
      {isEnabled && isEditing && (
        <div className={styles.configSection}>
          {/* General Settings Section */}
          <Body2 className={styles.sectionHeader}>General Settings</Body2>

          {/* OTP Toggle */}
          <Field label="Allow use of Microsoft Authenticator OTP">
            <Switch
              checked={isOtpEnabled}
              onChange={(_, data) => setIsOtpEnabled(data.checked)}
              aria-label="Allow use of Microsoft Authenticator OTP"
            />
          </Field>

          {/* Number Matching (Display-Only) */}
          <Field label="Number Matching">
            <Dropdown
              value="Enabled"
              selectedOptions={['enabled']}
              disabled
              aria-label="Number Matching"
            >
              <Option value="enabled">Enabled</Option>
            </Dropdown>
            <div className={styles.hintText}>
              Permanently enabled by Microsoft (October 2024)
            </div>
          </Field>

          {/* Display App Information */}
          <Field label="Show application name in push and passwordless notifications">
            <Dropdown
              value={displayAppInfoState === 'default' ? 'Microsoft managed' : displayAppInfoState === 'enabled' ? 'Enabled' : 'Disabled'}
              selectedOptions={[displayAppInfoState]}
              onOptionSelect={(_, data) => setDisplayAppInfoState(data.optionValue as string)}
              aria-label="Show application name in push and passwordless notifications"
            >
              <Option value="default">Microsoft managed</Option>
              <Option value="enabled">Enabled</Option>
              <Option value="disabled">Disabled</Option>
            </Dropdown>
          </Field>

          {/* Target Selection (shown when Display App Information is enabled or disabled) */}
          {displayAppInfoState !== 'default' && (
            <Field label="Target users">
              <Dropdown
                value={displayAppInfoTarget === 'all' ? 'All users' : 'Select group'}
                selectedOptions={[displayAppInfoTarget]}
                onOptionSelect={(_, data) => setDisplayAppInfoTarget(data.optionValue as string)}
                aria-label="Target users"
              >
                <Option value="all">All users</Option>
                <Option value="group">Select group</Option>
              </Dropdown>
            </Field>
          )}

          {/* Display Location Information */}
          <Field label="Show geographic location in push and passwordless notifications">
            <Dropdown
              value={displayLocationState === 'enabled' ? 'Enabled' : 'Disabled'}
              selectedOptions={[displayLocationState]}
              onOptionSelect={(_, data) => setDisplayLocationState(data.optionValue as string)}
              aria-label="Show geographic location in push and passwordless notifications"
            >
              <Option value="enabled">Enabled</Option>
              <Option value="disabled">Disabled</Option>
            </Dropdown>
          </Field>

          {/* Target Selection (shown when Display Location Information is enabled) */}
          {displayLocationState === 'enabled' && (
            <Field label="Target users">
              <Dropdown
                value={displayLocationTarget === 'all' ? 'All users' : 'Select group'}
                selectedOptions={[displayLocationTarget]}
                onOptionSelect={(_, data) => setDisplayLocationTarget(data.optionValue as string)}
                aria-label="Target users"
              >
                <Option value="all">All users</Option>
                <Option value="group">Select group</Option>
              </Dropdown>
            </Field>
          )}

          {/* Companion Applications */}
          <Field label="Microsoft Authenticator on companion applications">
            <Dropdown
              value={companionAppsState === 'default' ? 'Microsoft managed' : companionAppsState === 'enabled' ? 'Enabled' : 'Disabled'}
              selectedOptions={[companionAppsState]}
              onOptionSelect={(_, data) => setCompanionAppsState(data.optionValue as string)}
              aria-label="Microsoft Authenticator on companion applications"
            >
              <Option value="default">Microsoft managed</Option>
              <Option value="enabled">Enabled</Option>
              <Option value="disabled">Disabled</Option>
            </Dropdown>
          </Field>

          {/* Target Selection (shown when Companion Applications is enabled or disabled) */}
          {companionAppsState !== 'default' && (
            <Field label="Target users">
              <Dropdown
                value={companionAppsTarget === 'all' ? 'All users' : 'Select group'}
                selectedOptions={[companionAppsTarget]}
                onOptionSelect={(_, data) => setCompanionAppsTarget(data.optionValue as string)}
                aria-label="Target users"
              >
                <Option value="all">All users</Option>
                <Option value="group">Select group</Option>
              </Dropdown>
            </Field>
          )}

          {/* Save/Cancel Buttons */}
          <div className={styles.buttonGroup}>
            <Button appearance="primary" onClick={handleSave}>
              Save
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
          </div>
        </div>
      )}
    </BaseAuthenticationMethodCard>
  );
};
